import { FlatfileClient } from "@flatfile/api";
import { IBulkBatch } from "@wingspanhq/payments/dist/interfaces";
import { BATCH_LABELS_WS_KEYS } from "@wingspanhq/payments/dist/lib/constants";
import { CONTRACTORS_SHEET_SLUG, PAYABLES_SHEET_SLUG } from "../constants";
import { BulkResource } from "../types";

const MAIN_SHEET_SLUG_BY_RESOURCE: Record<BulkResource, string | undefined> = {
  [BulkResource.Payable]: PAYABLES_SHEET_SLUG,
  [BulkResource.Collaborator]: CONTRACTORS_SHEET_SLUG,
  // Calculate 1099 balances batches are not uploaded through Flatfile
  [BulkResource.Calculate1099]: undefined
};

/**
 * Id of the Flatfile sheet holding the uploaded spreadsheet rows of the given batch.
 *
 * A space holds two workbooks: the pinned one with the main data sheet the user uploads into
 * ("payables"/"contractors"), and "Wingspan Internal" with the `*-rejections` and `*-logs`
 * sheets. Only the latter is recorded in the space metadata (`wsInternalWorkbookId`), so the
 * main sheet is found by slug across every workbook of the space instead.
 */
export async function getFlatfileMainSheetId(
  flatfileApiClient: FlatfileClient,
  bulkBatch: IBulkBatch,
  bulkResource: BulkResource
): Promise<string> {
  const sheetSlug = MAIN_SHEET_SLUG_BY_RESOURCE[bulkResource];
  const spaceId = bulkBatch.labels?.[BATCH_LABELS_WS_KEYS.flatfileBatchId] as
    | string
    | undefined;

  if (!sheetSlug || !spaceId) {
    throw new Error(`No Flatfile sheet for ${bulkResource} batch`);
  }

  const { data: workbooks } = await flatfileApiClient.workbooks.list({
    spaceId
  });
  const mainSheet = workbooks
    .flatMap(workbook => workbook.sheets ?? [])
    .find(sheet => sheet.slug === sheetSlug);

  if (!mainSheet) {
    throw new Error(
      `Flatfile sheet "${sheetSlug}" not found in space ${spaceId}`
    );
  }

  return mainSheet.id;
}
